import { getFunctions, httpsCallable } from 'firebase/functions';
import firebaseApp from '@/config/firebase'; 
import { ColorPalette, CreateColorPalette, UpdateColorPalette, ColorPaletteWithPermissions } from '@shared/schemas/colorPalette';

const funcs = getFunctions(firebaseApp, 'europe-west2');

const managePalettesCallable = httpsCallable<{
  action: 'list' | 'create' | 'update' | 'delete' | 'getById' | 'getAccessible'; // Added getById & getAccessible
  paletteId?: string;
  payload?: Partial<CreateColorPalette> | Partial<UpdateColorPalette>;
  userId?: string; // For getAccessible
  organizationId?: string; // For getAccessible
}, { 
  success: boolean; 
  palettes?: ColorPalette[]; 
  palette?: ColorPalette; 
  paletteId?: string; 
  error?: any; 
  userPalettes?: ColorPaletteWithPermissions[]; // For getAccessible
  organizationPalettes?: ColorPaletteWithPermissions[]; // For getAccessible
}>(funcs, 'manageColorPalettes');

export const colorPaletteService = {
  list: async (): Promise<ColorPalette[]> => {
    try {
      const result = await managePalettesCallable({ action: 'list' });
      if (result.data.success && result.data.palettes) {
        return result.data.palettes;
      }
      console.error('Error listing palettes:', result.data.error);
      throw new Error(result.data.error || 'Failed to list palettes');
    } catch (error) {
      console.error('Firebase call failed (listPalettes):', error);
      throw error;
    }
  },

  getById: async (paletteId: string): Promise<ColorPalette | null> => {
    try {
      const result = await managePalettesCallable({ action: 'getById', paletteId });
      if (result.data.success && result.data.palette) {
        return result.data.palette;
      }
      console.warn('Palette not found or error getting by ID:', result.data.error);
      return null;
    } catch (error) {
      console.error('Firebase call failed (getPaletteById):', error);
      throw error;
    }
  },
  
  create: async (paletteData: CreateColorPalette): Promise<ColorPalette> => {
    try {
      const result = await managePalettesCallable({ action: 'create', payload: paletteData });
      if (result.data.success && result.data.palette) {
        return result.data.palette;
      }
      console.error('Error creating palette:', result.data.error);
      throw new Error(result.data.error || 'Failed to create palette');
    } catch (error) {
      console.error('Firebase call failed (createPalette):', error);
      throw error;
    }
  },

  update: async (paletteId: string, paletteData: UpdateColorPalette, userId: string): Promise<boolean> => { // userId often not needed for update if using auth context in backend
    try {
      // Include userId in payload if your backend expects it for permission checks during update, otherwise remove
      const payloadWithUserContext = { ...paletteData,  userId };
      const result = await managePalettesCallable({ action: 'update', paletteId, payload: payloadWithUserContext });
      if (!result.data.success) {
        console.error('Error updating palette:', result.data.error);
        throw new Error(result.data.error || 'Failed to update palette');
      }
      return result.data.success;
    } catch (error) {
      console.error('Firebase call failed (updatePalette):', error);
      throw error;
    }
  },

  delete: async (paletteId: string, userId: string): Promise<boolean> => { // userId often not needed for delete if using auth context in backend
    try {
       // Include userId in payload if backend expects it for permission checks, otherwise remove from payload
      const result = await managePalettesCallable({ action: 'delete', paletteId });
      if (!result.data.success) {
        console.error('Error deleting palette:', result.data.error);
        throw new Error(result.data.error || 'Failed to delete palette');
      }
      return result.data.success;
    } catch (error) {
      console.error('Firebase call failed (deletePalette):', error);
      throw error;
    }
  },
  
  getAccessiblePalettes: async (userId: string, organizationId: string): Promise<{ userPalettes: ColorPaletteWithPermissions[], organizationPalettes: ColorPaletteWithPermissions[] }> => {
    try {
      const result = await managePalettesCallable({ action: 'getAccessible', userId, organizationId });
      if (result.data.success) {
        return {
          userPalettes: result.data.userPalettes || [],
          organizationPalettes: result.data.organizationPalettes || [],
        };
      }
      console.error('Error fetching accessible palettes:', result.data.error);
      throw new Error(result.data.error || 'Failed to fetch accessible palettes');
    } catch (error) {
      console.error('Firebase call failed (getAccessiblePalettes):', error);
      throw error;
    }
  },
}; 

// These individual exports were used by usePalettes hook previously.
// Consolidating to use colorPaletteService.list etc. might be cleaner, but this maintains compatibility for now.
export const listPalettes = colorPaletteService.list;
export const createPalette = colorPaletteService.create;
export const updatePalette = (paletteId: string, paletteData: UpdateColorPalette) => colorPaletteService.update(paletteId, paletteData, 'dummyUserIdNeedProperContext'); // needs context for userId
export const deletePalette = (paletteId: string) => colorPaletteService.delete(paletteId, 'dummyUserIdNeedProperContext'); // needs context for userId 